import { useCallback, useEffect, useState } from 'react';
import './events.css';
import EventsTable from './EventsTable';
import EventDetailModal from './EventDetailModal';
import EventsCardsGrid from './EventsCardsGrid';
import RaisedPage from '@atoms/RaisedPage';
import { useNavigate, useSearchParams } from 'react-router';
import EventProvider from '@providers/EventProvider';
import { Column } from '@material-table/core';
import { EventContextValues } from '@hooks/useEventContext';
import { EventRoleWithMemberships } from '@modules/supabase/events';

interface Props {
    columns?: Column<EventRoleWithMemberships>[];
    fetchEvents: () => Promise<EventRoleWithMemberships[]>;
    managingEvents?: boolean;
}
export default function EventsList({ columns, fetchEvents, managingEvents }: Props) {
    const navigate = useNavigate();
    const [viewType, setViewType] = useState<'table' | 'card'>('table');
    const [events, setEvents] = useState<EventRoleWithMemberships[] | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<EventContextValues['event'] | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const onSelectEvent = useCallback(
        (event: EventContextValues['event']) => {
            if (managingEvents) {
                navigate(`/events/${event?.id || ''}`);
            } else {
                setSelectedEvent(event);
                setSearchParams({ eventId: event?.id as string });
            }
        },
        [managingEvents, navigate, setSearchParams],
    );

    const refreshEvents = useCallback(() => fetchEvents().then(setEvents), [fetchEvents]);

    const closeAndRefreshEvents = useCallback(() => {
        if (searchParams.has('eventId') && searchParams.get('eventId')) {
            searchParams.delete('eventId');
            setSearchParams(searchParams);
        }
        // refresh events after closing the event modal
        refreshEvents();
    }, [refreshEvents, searchParams, setSearchParams]);

    useEffect(() => {
        refreshEvents();
    }, [refreshEvents]);

    // Restore selected event from URL
    useEffect(() => {
        if (searchParams.has('eventId')) {
            const eventId = searchParams.get('eventId');
            const selectedEvent = events?.find((e) => e.id === eventId);
            if (selectedEvent) {
                setSelectedEvent(selectedEvent as unknown as EventContextValues['event']);
            }
        }
        // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events?.length, searchParams]);

    return (
        <RaisedPage loading={events === null}>
            {viewType === 'table' ? (
                <EventsTable
                    events={events}
                    loading={events === null}
                    onSelectEvent={onSelectEvent}
                    columns={columns}
                    setViewType={setViewType}
                />
            ) : (
                <EventsCardsGrid
                    events={events}
                    onSelectEvent={onSelectEvent}
                    setViewType={setViewType}
                />
            )}
            <EventProvider event={selectedEvent}>
                <EventDetailModal
                    onClose={() => {
                        setSelectedEvent(null);
                        closeAndRefreshEvents();
                    }}
                    open={selectedEvent !== null}
                />
            </EventProvider>
        </RaisedPage>
    );
}
